export type SettingsPath =
  | "/settings/appearance"
  | "/settings/snap-shot"
  | "/settings/integrations"
  | "/settings/general"
  | "/settings/projects"
  | "/settings/keybindings"
  | "/settings/providers"
  | "/settings/mcp-servers"
  | "/settings/source-control"
  | "/settings/worktrees"
  | "/settings/admin"
  | "/settings/account"
  | "/settings/connections"
  | "/settings/network"
  | "/settings/organizations"
  | "/settings/updates"
  | "/settings/feedback"
  | "/settings/archived"
  | "/settings/diagnostics";

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly to: SettingsPath;
  readonly targetId?: string;
  readonly keywords?: ReadonlyArray<string>;
  /** The Connections search parameter naming the Environment page the row lives on. */
  readonly search?: { readonly environment: string };
}

export const SETTINGS_SECTION_LABELS: Readonly<Record<SettingsPath, string>> = {
  "/settings/general": "General",
  "/settings/projects": "Projects",
  "/settings/appearance": "Appearance",
  "/settings/snap-shot": "SnapShots",
  "/settings/integrations": "Integrations",
  "/settings/keybindings": "Keybindings",
  "/settings/providers": "Providers",
  "/settings/mcp-servers": "MCP servers",
  "/settings/source-control": "Source Control",
  "/settings/worktrees": "Worktrees",
  "/settings/admin": "Administration",
  "/settings/account": "Account",
  "/settings/connections": "Connections",
  "/settings/network": "Network",
  "/settings/organizations": "Organizations",
  "/settings/updates": "Updates",
  "/settings/feedback": "Feedback",
  "/settings/archived": "Archive",
  "/settings/diagnostics": "Diagnostics",
};

export const SETTINGS_SEARCH_ITEMS = [
  {
    id: "admin",
    title: "Administration",
    to: "/settings/admin",
    keywords: ["accounts", "allowance", "grant", "usage"],
  },
  {
    id: "projects",
    title: "Projects",
    to: "/settings/projects",
    keywords: ["project", "checkout", "actions", "icon", "rename"],
  },
  {
    id: "organization",
    title: "Organization",
    to: "/settings/general",
    keywords: ["sidebar", "sorting", "order", "grouping"],
  },
  {
    id: "project-grouping",
    title: "Project grouping",
    to: "/settings/general",
    keywords: ["repository", "checkout", "group"],
  },
  {
    id: "auto-settle-merged-threads",
    title: "Auto-settle merged threads",
    to: "/settings/general",
    keywords: ["settle", "merge", "pull request"],
  },
  {
    id: "auto-settle-inactive-threads",
    title: "Auto-settle inactive threads",
    to: "/settings/general",
    keywords: ["settle", "inactive", "stale"],
  },
  {
    id: "days-before-auto-settle",
    title: "Days before auto-settle",
    to: "/settings/general",
    targetId: "auto-settle-inactive-threads",
    keywords: ["settle", "days"],
  },
  {
    id: "thread-order",
    title: "Thread order",
    to: "/settings/general",
    keywords: ["sidebar", "sorting"],
  },
  {
    // The scheme tiles sit at the top of the Appearance section.
    id: "color-scheme",
    title: "Color scheme",
    to: "/settings/appearance",
    targetId: "appearance",
    keywords: ["appearance", "light", "dark", "system", "mode"],
  },
  {
    // Theme cards live directly under the scheme tiles; the section is the
    // stable scroll destination for both.
    id: "theme",
    title: "Themes",
    to: "/settings/appearance",
    targetId: "appearance",
    keywords: ["appearance", "colors", "palette", "custom", "import"],
  },
  {
    // Prefixed because the slider control already owns the `appearance-contrast` id.
    id: "setting-appearance-contrast",
    title: "Contrast",
    to: "/settings/appearance",
    keywords: ["colors", "borders", "interface"],
  },
  {
    // Prefixed because the slider control already owns the `glass-opacity` id.
    id: "setting-glass-opacity",
    title: "Glass opacity",
    to: "/settings/appearance",
    keywords: ["transparent", "transparency", "solid", "menus", "dialogs", "composer"],
  },
  {
    id: "diff-color-scheme",
    title: "Diff colors",
    to: "/settings/appearance",
    keywords: ["red", "green", "blue", "orange", "additions", "deletions", "colorblind"],
  },
  {
    id: "panel-animations",
    title: "Panel animations",
    to: "/settings/appearance",
    keywords: ["motion", "transition", "duration"],
  },
  {
    id: "interface-font",
    title: "Interface font",
    to: "/settings/appearance",
    keywords: ["typography", "family", "size", "system", "sans"],
  },
  {
    id: "prompt-font",
    title: "Prompt font",
    to: "/settings/appearance",
    keywords: ["typography", "family", "size", "composer", "input"],
  },
  {
    id: "code-font",
    title: "Code font",
    to: "/settings/appearance",
    keywords: ["typography", "family", "size", "monospace", "diffs", "file previews"],
  },
  {
    id: "terminal-font",
    title: "Terminal font",
    to: "/settings/appearance",
    keywords: ["typography", "family", "size", "monospace", "output"],
  },
  {
    id: "font-smoothing",
    title: "Font smoothing",
    to: "/settings/appearance",
    keywords: ["typography", "text", "grayscale", "anti aliasing", "macos", "thin"],
  },
  {
    id: "word-wrap",
    title: "Word wrap",
    to: "/settings/appearance",
    keywords: ["long lines", "code blocks", "tables", "diffs", "file previews"],
  },
  {
    id: "browser",
    title: "Browser",
    to: "/settings/integrations",
    keywords: ["integrations", "viewport", "zoom", "recording", "preview"],
  },
  {
    id: "browser-link-target",
    title: "Open links in",
    to: "/settings/integrations",
    keywords: ["link", "links", "external", "default browser", "chat", "terminal"],
  },
  {
    id: "browser-profiles",
    title: "Browser profiles",
    to: "/settings/integrations",
    keywords: [
      "profile",
      "cookies",
      "logins",
      "incognito",
      "import",
      "chrome",
      "safari",
      "firefox",
    ],
  },
  {
    id: "snap-shot-enabled",
    title: "SnapShots",
    to: "/settings/snap-shot",
    keywords: ["window", "capture", "screenshot"],
  },
  {
    id: "snap-shot-accessibility",
    title: "Include app text",
    to: "/settings/snap-shot",
    targetId: "snap-shot-enabled",
    keywords: ["capture", "accessibility", "text", "elements", "agent context"],
  },
  {
    id: "snap-shot-shortcut",
    title: "Capture shortcut",
    to: "/settings/snap-shot",
    targetId: "snap-shot-enabled",
  },
  {
    id: "snap-shot-sound",
    title: "Capture sound",
    to: "/settings/snap-shot",
    targetId: "snap-shot-enabled",
  },
  {
    id: "snap-shot-flash",
    title: "Capture flash",
    to: "/settings/snap-shot",
    targetId: "snap-shot-enabled",
  },
  {
    id: "snap-shot-animations",
    title: "Capture animations",
    to: "/settings/snap-shot",
    targetId: "snap-shot-enabled",
  },
  { id: "time-format", title: "Time format", to: "/settings/general" },
  {
    id: "hide-whitespace-changes",
    title: "Hide whitespace changes",
    to: "/settings/general",
  },
  {
    id: "default-agent-provider",
    title: "Default Agent Provider",
    to: "/settings/general",
  },
  {
    id: "response-streaming",
    title: "Response streaming",
    to: "/settings/general",
    keywords: ["streaming", "tokens", "paragraph", "assistant output"],
  },
  { id: "new-threads", title: "New threads", to: "/settings/general" },
  {
    id: "start-from-origin",
    title: "Start from origin",
    to: "/settings/general",
    targetId: "new-threads",
  },
  {
    id: "add-project-starts-in",
    title: "Add project starts in",
    to: "/settings/general",
  },
  {
    id: "default-diff-file-state",
    title: "Default diff file state",
    to: "/settings/general",
    keywords: ["diff", "collapsed", "expanded"],
  },
  {
    id: "diff-layout",
    title: "Diff layout",
    to: "/settings/general",
    keywords: ["diff", "split", "stacked", "side by side"],
  },
  {
    id: "in-app-notifications",
    title: "In-app notifications",
    to: "/settings/general",
    keywords: ["toast", "notify", "alert"],
  },
  {
    id: "proactive-panels",
    title: "Proactive panels",
    to: "/settings/general",
    keywords: ["panel", "diff", "pull request"],
  },
  {
    id: "follow-up-behavior",
    title: "Follow-up behavior",
    to: "/settings/general",
    keywords: ["queue", "steer", "follow up"],
  },
  {
    id: "plan-sidebar",
    title: "Plan panel",
    to: "/settings/general",
    keywords: ["plan", "panel", "sidebar"],
  },
  {
    id: "context-window-meter",
    title: "Context window meter",
    to: "/settings/general",
    keywords: ["tokens", "context", "usage"],
  },
  {
    id: "skills-in-slash-menu",
    title: "Skills in slash menu",
    to: "/settings/general",
    keywords: ["slash", "command", "skills"],
  },
  {
    id: "composer-rich-text",
    title: "Rich text composer",
    to: "/settings/general",
    keywords: ["markdown", "composer", "formatting"],
  },
  {
    id: "composer-collapse",
    title: "Collapse composer on scroll",
    to: "/settings/general",
    keywords: ["composer", "scroll"],
  },
  {
    id: "send-shortcut",
    title: "Send shortcut",
    to: "/settings/general",
    keywords: ["enter", "send", "newline"],
  },
  {
    id: "provider-update-checks",
    title: "Provider update checks",
    to: "/settings/general",
    keywords: ["provider", "version", "update"],
  },
  {
    id: "continue-threads-after-server-update",
    title: "Continue threads after restarts",
    to: "/settings/general",
    keywords: ["restart", "recovery", "resume"],
  },
  {
    id: "background-activity",
    title: "Background activity",
    to: "/settings/general",
    keywords: ["battery", "performance", "idle", "background"],
  },
  {
    id: "always-ingest-attachments",
    title: "Always ingest attachments",
    to: "/settings/general",
    keywords: ["brain", "ingest", "attach", "files", "folders"],
  },
  {
    id: "agent-browser-access",
    title: "Agent browser access",
    to: "/settings/general",
    keywords: ["browser", "agent"],
  },
  {
    id: "unpin-confirmation",
    title: "Unpin confirmation",
    to: "/settings/general",
    keywords: ["pin", "confirm"],
  },
  {
    id: "archive-confirmation",
    title: "Archive confirmation",
    to: "/settings/general",
  },
  {
    id: "delete-confirmation",
    title: "Delete confirmation",
    to: "/settings/general",
  },
  {
    id: "quit-confirmation",
    title: "Quit shortcut",
    to: "/settings/general",
    keywords: ["quit", "exit", "confirm"],
  },
  {
    id: "text-generation-model",
    title: "Text generation model",
    to: "/settings/general",
  },
  { id: "keybindings", title: "Keybindings", to: "/settings/keybindings" },
  {
    id: "providers",
    title: "Providers",
    to: "/settings/providers",
    keywords: ["Codex", "compaction", "Goal"],
  },
  {
    id: "mcp-servers",
    title: "MCP servers",
    to: "/settings/mcp-servers",
    keywords: ["MCP", "servers", "tools", "add server", "delete server", "enable", "disable"],
  },
  {
    id: "automatic-pull",
    title: "Automatically pull",
    to: "/settings/source-control",
    keywords: ["auto pull", "default branch", "fast forward", "upstream", "checkout", "behind"],
  },
  {
    id: "push-authorship-notes",
    title: "Push authorship notes",
    to: "/settings/source-control",
    keywords: ["git-ai", "authorship", "contributions", "notes", "push", "agent trace"],
  },
  {
    id: "pull-request-merge-method",
    title: "Default merge method",
    to: "/settings/source-control",
    keywords: ["pull request", "merge", "squash", "rebase", "last selected"],
  },
  {
    id: "source-control",
    title: "Source Control",
    to: "/settings/source-control",
    keywords: ["version control", "git", "GitHub", "hosting", "credentials", "scan"],
  },
  {
    id: "git-fetch-interval",
    title: "Git fetch interval",
    to: "/settings/source-control",
    keywords: ["automatic", "remote branch", "refresh", "background", "seconds", "off"],
  },
  {
    id: "source-control-writing-style",
    title: "Source control writing style",
    to: "/settings/source-control",
    keywords: [
      "repository conventions",
      "conventional commits",
      "custom instructions",
      "commit message",
      "pull request title",
    ],
  },
  {
    id: "follow-pull-request-templates",
    title: "Follow pull request templates",
    to: "/settings/source-control",
    keywords: ["repository", "pull request", "description", "template"],
  },
  {
    id: "source-control-writer-model",
    title: "Source control writer model",
    to: "/settings/source-control",
    keywords: ["commit message", "pull request", "branch name", "generated", "model"],
  },
  {
    id: "managed-thread-worktrees",
    title: "Managed Thread Worktrees",
    to: "/settings/worktrees",
  },
  { id: "account", title: "Account", to: "/settings/account" },
  {
    id: "cloud-plan",
    title: "Cloud plan",
    to: "/settings/account",
    keywords: ["usage", "limit", "allowance", "subscription", "remaining"],
  },
  { id: "connections", title: "Connections", to: "/settings/connections" },
  {
    id: "network",
    title: "Network",
    to: "/settings/network",
    keywords: ["afar network", "self-hosted", "machine", "enroll", "enrollment code", "computer"],
  },
  {
    id: "organizations",
    title: "Organizations",
    to: "/settings/organizations",
    keywords: ["organization", "invite", "member", "owner", "team", "shared machines"],
  },
  {
    id: "local-environment",
    title: "Local environment",
    to: "/settings/connections",
    search: { environment: "this-machine" },
    keywords: ["this machine", "environment service", "remote only", "turn off", "local server"],
  },
  {
    id: "remote-environments",
    title: "Environments",
    to: "/settings/connections",
    keywords: [
      "add environment",
      "ssh",
      "pair",
      "pairing code",
      "remote link",
      "machines",
      "cloud workspace",
    ],
  },
  {
    id: "load-balancing",
    title: "Automatic placement",
    to: "/settings/connections",
    keywords: ["load balancing", "thread placement", "share"],
  },
  {
    id: "network-access",
    title: "Network access",
    to: "/settings/connections",
    search: { environment: "this-machine" },
    keywords: ["lan", "local network", "expose", "reachable", "listener"],
  },
  {
    id: "tailscale-https",
    title: "Tailscale HTTPS",
    to: "/settings/connections",
    search: { environment: "this-machine" },
    keywords: ["tailscale", "tailnet", "serve", "https", "magicdns", "remote"],
  },
  { id: "updates", title: "Updates", to: "/settings/updates" },
  { id: "feedback", title: "Feedback", to: "/settings/feedback" },
  { id: "archive", title: "Archived threads", to: "/settings/archived" },
  { id: "diagnostics", title: "Diagnostics", to: "/settings/diagnostics" },
] as const satisfies ReadonlyArray<SettingsSearchItem>;

export type SettingsSearchItemId = (typeof SETTINGS_SEARCH_ITEMS)[number]["id"];

/**
 * The rows that only exist in the Desktop, because the controls behind them are Desktop bridge
 * methods.
 *
 * Filtered out of search rather than left to resolve to nothing: they point at
 * /settings/connections, which is always there, so the path check every other entry relies on
 * cannot answer for these.
 */
const DESKTOP_ONLY_SEARCH_ITEM_IDS: ReadonlySet<SettingsSearchItemId> = new Set([
  "local-environment",
  "network-access",
  "tailscale-https",
]);

export function settingsSearchItems(input: {
  readonly serverExposureAvailable: boolean;
}): ReadonlyArray<SettingsSearchItem> {
  if (input.serverExposureAvailable) return SETTINGS_SEARCH_ITEMS;
  return SETTINGS_SEARCH_ITEMS.filter((item) => !DESKTOP_ONLY_SEARCH_ITEM_IDS.has(item.id));
}

export function searchableSetting(id: SettingsSearchItemId): {
  readonly id: SettingsSearchItemId;
  readonly title: string;
} {
  const item = SETTINGS_SEARCH_ITEMS.find((candidate) => candidate.id === id);
  if (item === undefined) {
    throw new Error(`Unknown searchable setting: ${id}`);
  }
  return { id: item.id, title: item.title };
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Matches for `query`, in catalog order. Given the sections a client offers, in sidebar order,
 * results are limited to those sections and listed in that order, catalog order within each.
 */
export function searchSettings(
  query: string,
  items: ReadonlyArray<SettingsSearchItem> = SETTINGS_SEARCH_ITEMS,
  availablePaths?: ReadonlySet<SettingsPath>,
): ReadonlyArray<SettingsSearchItem> {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  const matches = items.filter(
    (item) =>
      (availablePaths === undefined || availablePaths.has(item.to)) &&
      [item.title, ...(item.keywords ?? [])].some((value) =>
        normalizeSearchText(value).includes(normalizedQuery),
      ),
  );
  if (availablePaths === undefined) return matches;
  const sectionRank = new Map([...availablePaths].map((path, index) => [path, index]));
  return matches.toSorted((a, b) => (sectionRank.get(a.to) ?? 0) - (sectionRank.get(b.to) ?? 0));
}
